import React, { useRef, useState, useEffect } from 'react';
import { useReactFlow, useViewport } from 'reactflow';
import {
  StickyNote,
  Type,
  ImagePlus,
  FileText,
  Link2,
  ZoomIn,
  ZoomOut,
  Maximize,
  Check,
  X,
  Wrench,
  ChevronDown,
  Magnet,
  House,
} from 'lucide-react';

export interface BoardToolItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

interface BoardToolbarProps {
  onAddNote: () => void;
  onAddText: () => void;
  onUploadFiles: (files: File[]) => void;
  onAddLink: (url: string) => void;
  onOpenSnapshots?: () => void;
  onAddFloorPlan: () => void;
  tools?: BoardToolItem[];
  snapEnabled?: boolean;
  onToggleSnap?: () => void;
}

const BoardToolbar: React.FC<BoardToolbarProps> = ({
  onAddNote,
  onAddText,
  onUploadFiles,
  onAddLink,
  onOpenSnapshots,
  onAddFloorPlan,
  tools = [],
  snapEnabled = true,
  onToggleSnap,
}) => {
  const { zoomIn, zoomOut, fitView } = useReactFlow();
  const { zoom } = useViewport();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toolsRef = useRef<HTMLDivElement>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkValue, setLinkValue] = useState('');
  const [toolsOpen, setToolsOpen] = useState(false);

  useEffect(() => {
    if (!toolsOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target as Node)) {
        setToolsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [toolsOpen]);

  const submitLink = () => {
    const v = linkValue.trim();
    if (v) onAddLink(v);
    setLinkValue('');
    setLinkOpen(false);
  };

  return (
    <>
      <div className="board-toolbar-dock">
      <div className="board-toolbar">
        <button type="button" className="board-tool" title="Add image" onClick={() => fileInputRef.current?.click()}>
          <ImagePlus size={18} />
          <span className="board-tool-label">Image</span>
        </button>
        <button
          type="button"
          className="board-tool"
          title="Add a scaled floor plan"
          onClick={onAddFloorPlan}
        >
          <House size={18} />
          <span className="board-tool-label">Floor plan</span>
        </button>
        {onOpenSnapshots && (
          <button
            type="button"
            className="board-tool"
            title="Add a snapshot from this project's 2D plans or 3D model"
            onClick={onOpenSnapshots}
          >
            <FileText size={18} />
            <span className="board-tool-label">Plans / Model</span>
          </button>
        )}
        <button type="button" className="board-tool" title="Add link" onClick={() => setLinkOpen((o) => !o)}>
          <Link2 size={18} />
          <span className="board-tool-label">Link</span>
        </button>
        <button type="button" className="board-tool" title="Add sticky note" onClick={onAddNote}>
          <StickyNote size={18} />
          <span className="board-tool-label">Note</span>
        </button>
        <button type="button" className="board-tool" title="Add text" onClick={onAddText}>
          <Type size={18} />
          <span className="board-tool-label">Text</span>
        </button>

        {tools.length > 0 && (
          <>
            <span className="board-toolbar-sep" />
            <div className="board-tools-menu-wrap" ref={toolsRef}>
              <button
                type="button"
                className={`board-tool board-tools-trigger${toolsOpen ? ' is-open' : ''}`}
                title="Board tools"
                onClick={() => setToolsOpen((o) => !o)}
              >
                <Wrench size={18} />
                <span className="board-tool-label">Tools</span>
                <ChevronDown size={14} className="board-tools-chevron" />
              </button>
              {toolsOpen && (
                <div className="board-tools-dropdown" role="menu">
                  {tools.map((tool) => (
                    <button
                      key={tool.id}
                      type="button"
                      role="menuitem"
                      className="board-tools-item"
                      disabled={tool.disabled}
                      onClick={() => {
                        if (!tool.disabled) {
                          tool.onClick();
                          setToolsOpen(false);
                        }
                      }}
                    >
                      <span className="board-tools-item-icon">{tool.icon}</span>
                      {tool.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <span className="board-toolbar-sep" />

        {onToggleSnap && (
          <button
            type="button"
            className={`board-tool icon-only${snapEnabled ? ' is-active' : ''}`}
            title={snapEnabled ? 'Snapping on — hold Alt to bypass' : 'Snapping off'}
            aria-pressed={snapEnabled}
            onClick={onToggleSnap}
          >
            <Magnet size={18} />
          </button>
        )}
        <button type="button" className="board-tool icon-only" title="Zoom out" onClick={() => zoomOut()}>
          <ZoomOut size={18} />
        </button>
        <button
          type="button"
          className="board-tool zoom-pct"
          title="Fit to view"
          onClick={() => fitView({ padding: 0.2, duration: 300 })}
        >
          {Math.round((zoom || 1) * 100)}%
        </button>
        <button type="button" className="board-tool icon-only" title="Zoom in" onClick={() => zoomIn()}>
          <ZoomIn size={18} />
        </button>
        <button
          type="button"
          className="board-tool icon-only"
          title="Fit to view"
          onClick={() => fitView({ padding: 0.2, duration: 300 })}
        >
          <Maximize size={18} />
        </button>
      </div>
      </div>

      {linkOpen && (
        <div className="board-link-popover">
          <input
            autoFocus
            type="url"
            placeholder="Paste a link (https://…)"
            value={linkValue}
            onChange={(e) => setLinkValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitLink();
              if (e.key === 'Escape') {
                setLinkValue('');
                setLinkOpen(false);
              }
            }}
          />
          <button type="button" className="board-link-add" title="Add link" onClick={submitLink}>
            <Check size={16} />
          </button>
          <button
            type="button"
            className="board-link-cancel"
            title="Cancel"
            onClick={() => {
              setLinkValue('');
              setLinkOpen(false);
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length) onUploadFiles(files);
          e.target.value = '';
        }}
      />
    </>
  );
};

export default BoardToolbar;
